import { Link as PortalsLink } from '@portals/core/src/components/Link/Link';
import { ArrowRightSmall, Download, ExternalLink, MailOutline, Pdf } from '@portals/icons';
import { Telephone } from '@portals/icons/src/Telephone/Telephone';
import i18next from 'i18next';
import React, { PropsWithChildren } from 'react';

import { File, isLinkAssetLink, isLinkExternalLink, isLinkInternalLink } from '../../../models';
import { Link as LinkModel, useLinkProps } from '../../hooks/useLinkProps';
import { convertSizeToDisplayValue, getFileTypeFromMime } from '../../utils/fileUtils';
interface Props {
  link: LinkModel;
}

const getDisplayStringFromFile = (file: File) =>
  file.size && file.mime ? `(${getFileTypeFromMime(file.mime)} ${convertSizeToDisplayValue(file.size)})` : '';

// eslint-disable-next-line import/no-named-as-default-member
const t = i18next.getFixedT.bind(i18next)(null, 'root', 'link');

export const Link: React.FC<PropsWithChildren<Props>> = ({ link, children }) => {
  const { singleProps } = useLinkProps(link);

  // Note: Abweichend zum Model sind für RichTextLinks der Title im Feld "text" abgespeichert
  const title = singleProps?.title || (link.text !== children && link.text ? link.text : undefined);

  const getAriaLabel = (labelKey: string, fileDisplay: string = ''): string => {
    const ariaTitle = link.text && link.text !== children ? link.text : typeof children === 'string' ? children : '';
    const fileDisplayString = fileDisplay ? ` ${fileDisplay}` : '';

    return `${ariaTitle ? `${ariaTitle}${fileDisplayString} - ` : ''}${t(labelKey)}`;
  };

  if (isLinkAssetLink(link) && getFileTypeFromMime(link.media.mime) === 'PDF') {
    const fileDisplay = getDisplayStringFromFile(link.media);

    return (
      <PortalsLink
        {...singleProps}
        title={title}
        icon={<Pdf />}
        aria-label={getAriaLabel('ariaLabel.assetLinkPdf', fileDisplay)}
      >
        {children} {getDisplayStringFromFile(link.media)}
      </PortalsLink>
    );
  }
  if (isLinkAssetLink(link)) {
    const fileDisplay = getDisplayStringFromFile(link.media);

    return (
      <PortalsLink
        {...singleProps}
        title={title}
        icon={<Download />}
        aria-label={getAriaLabel('ariaLabel.assetLink', fileDisplay)}
      >
        {children} {getDisplayStringFromFile(link.media)}
      </PortalsLink>
    );
  }

  if (isLinkExternalLink(link) && singleProps?.href?.startsWith('tel:')) {
    return (
      <PortalsLink
        {...singleProps}
        title={title}
        icon={<Telephone />}
        aria-label={getAriaLabel('ariaLabel.externalLinkPhone')}
      >
        {children}
      </PortalsLink>
    );
  }

  if (isLinkExternalLink(link) && singleProps?.href?.startsWith('mailto:')) {
    return (
      <PortalsLink
        {...singleProps}
        title={title}
        icon={<MailOutline />}
        aria-label={getAriaLabel('ariaLabel.externalLinkEmail')}
      >
        {children}
      </PortalsLink>
    );
  }

  if (isLinkExternalLink(link)) {
    return (
      <PortalsLink
        {...singleProps}
        title={title}
        icon={<ExternalLink />}
        aria-label={getAriaLabel('ariaLabel.externalLink')}
      >
        {children}
      </PortalsLink>
    );
  }

  if (isLinkInternalLink(link)) {
    return (
      <PortalsLink
        {...singleProps}
        title={title}
        icon={<ArrowRightSmall />}
        aria-label={getAriaLabel('ariaLabel.internalLink')}
      >
        {children}
      </PortalsLink>
    );
  }

  return (
    <PortalsLink {...singleProps} title={title}>
      {children}
    </PortalsLink>
  );
};
